import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, Subject, Subscription } from 'rxjs';

import { TValue } from 'src/app/models/generic';

@Component({
  selector: 'app-c-input',
  templateUrl: './c-input.component.html',
  styleUrls: ['./c-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CInputComponent)
    }
  ]
})
export class CInputComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  private static nextId = 0;
  readonly inputId = `c-input-${CInputComponent.nextId++}`;
  readonly hintId = `${this.inputId}-hint`;
  readonly errorId = `${this.inputId}-error`;

  onTouched = (): void => {};
  onChange = (value: TValue): void => {};

  @Input()
  label = ' ';

  @Input()
  placeholder: string | undefined = ' ';

  @Input()
  inputType = 'text' || 'textarea = text';

  @Input()
  required = false;

  @Input()
  inputMask: string | undefined = '';

  @Input()
  separatorLimit: string = '100000000000';

  @Input()
  currencyMask = false;

  @Input()
  dropSpecial = true;

  @Input()
  readonly = false;

  @Input()
  disabled = false;

  @Input()
  value: TValue;

  @Input()
  isParameter: boolean = false;

  @Input()
  isProduct: boolean = false;

  @Input()
  combo: boolean = false;

  @Input()
  index: number = 0;

  @Input()
  rows: number = 4;

  @Input()
  maxlength: number = 1000;

  @Input()
  uppercase: boolean = false;

  @Input()
  appearance: 'legacy' | 'modern' = 'legacy';

  @Input()
  hint: string = '';

  @Input()
  errorMessage: string = '';

  @Input()
  hasError: boolean = false;

  // Computed mask for CPF/CNPJ (supports alphanumeric CNPJ)
  computedMask: string = '';
  isValuePreFormatted: boolean = false;
  useMask: boolean = true;

  constructor() {}

  get hasErrorState(): boolean {
    return this.hasError;
  }

  get currentErrorMessage(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }

    return 'Revise este campo.';
  }

  get describedBy(): string | null {
    if (this.hasErrorState) {
      return this.errorId;
    }

    if (this.hint) {
      return this.hintId;
    }

    return null;
  }

  writeValue(value: TValue): void {
    // Se for CPF_CNPJ, desabilita a máscara do ngx-mask e usa custom
    if (this.inputMask === 'CPF_CNPJ') {
      this.useMask = false;
      if (value) {
        const stringValue = String(value);
        // Verifica se já tem formatação (contém . / ou -)
        this.isValuePreFormatted = /[./-]/.test(stringValue);
        //console.log('CPF_CNPJ writeValue:', { value, stringValue, isValuePreFormatted: this.isValuePreFormatted });

        // Se não estiver formatado, formata agora
        if (!this.isValuePreFormatted) {
          this.value = this.formatCpfCnpj(stringValue);
          //console.log('Formatado para:', this.value);
        } else {
          this.value = value;
        }
      } else {
        this.isValuePreFormatted = false;
        this.value = value;
      }
    } else {
      this.useMask = true;
      this.isValuePreFormatted = false;
      this.value = value;
    }

    // Atualiza a máscara imediatamente após definir o valor
    this.updateMask();

    setTimeout(() => {
      if (this.currencyMask) {
        this.initCurrencyMaskInput();
      }
    }, 150);
  }

  initCurrencyMaskInput(): void {
    let element = document.getElementById(this.inputId) as HTMLInputElement;

    if (element?.value) {
      element.value = parseFloat((this.value as any) ?? 0).toLocaleString(
        'pt-BR',
        {
          style: 'currency',
          currency: 'BRL'
        }
      );
    }
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.updateMask();
  }

  ngOnChanges(): void {
    this.updateMask();
  }

  updateMask(): void {
    // Se a máscara for CPF_CNPJ, usa a lógica customizada
    if (this.inputMask === 'CPF_CNPJ') {
      this.computedMask = this.getCpfCnpjMask();
    } else {
      this.computedMask = this.inputMask || '';
    }
  }

  getCpfCnpjMask(): string {
    if (!this.value) {
      // Se não tem valor, permite tanto CPF quanto CNPJ
      // * = qualquer caractere (letra ou número)
      return '***.***.***-**||**.***.***/****-**';
    }

    const stringValue = String(this.value);
    // Remove caracteres especiais para contar
    const cleanValue = stringValue.replace(/[^\w]/g, '');
    const length = cleanValue.length;

    //console.log('getCpfCnpjMask:', { value: this.value, cleanValue, length });

    // CPF tem 11 caracteres
    if (length <= 11) {
      //console.log('Aplicando máscara CPF: ***.***.***-**');
      return '***.***.***-**';
    }

    // CNPJ tem 14 caracteres (aceita alfanumérico)
    // * aceita qualquer caractere
    //console.log('Aplicando máscara CNPJ: **.***.***/****-**');
    return '**.***.***/****-**';
  }

  transformCurrency(): void {
    let element = document.getElementById(this.inputId) as HTMLInputElement;

    if (element?.value) {
      let newValueString: string = element.value.replace(/\D+/g, '');
      let beforeDot: string = newValueString.substring(
        0,
        newValueString.length - 2
      );
      let afterDot: string = newValueString.substring(
        newValueString.length - 2,
        newValueString.length
      );

      let valueNumber: any = Number(beforeDot + '.' + afterDot);

      if (!valueNumber) valueNumber = 0;

      element.value = parseFloat(valueNumber).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
      this.onChange(Number(valueNumber));
    } else {
      element.value = parseFloat('0').toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    }
  }

  checkLastNumber(valueChange: string, valueInput: string): boolean {
    return !valueChange.endsWith('0') && valueInput.endsWith('0');
  }

  // Formata CPF/CNPJ manualmente (aceita alfanumérico)
  formatCpfCnpj(value: string): string {
    if (!value) return '';

    // Remove formatação existente e converte para maiúsculo
    const clean = value.replace(/[^\w]/g, '').toUpperCase();

    if (clean.length <= 11) {
      // CPF: 000.000.000-00
      return clean
        .replace(/(\w{3})(\w{3})(\w{3})(\w{2})/, '$1.$2.$3-$4')
        .substring(0, 14);
    } else {
      // CNPJ: 00.000.000/0000-00
      return clean
        .replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})/, '$1.$2.$3/$4-$5')
        .substring(0, 18);
    }
  }  // Evento de input para CPF/CNPJ
  onCpfCnpjInput(event: any): void {
    let value = event.target.value;
    const formatted = this.formatCpfCnpj(value);

    // Atualiza o valor formatado
    this.value = formatted;
    this.onChange(formatted);

    // Atualiza o input
    event.target.value = formatted;
  }
}
